import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useUncompleteTask,
  newIdempotencyKey,
  familyKeys,
  type DayTasksResponse,
  type MissionsResponse,
  type OccurrenceResponse,
  type ChildBalanceResponse,
} from '@kidshome/api-contract';
import { invalidateHeroSurfaces } from '@/entities/hero';

/**
 * `uncomplete-task` — the kid's UNDO (ADR-0141), the mirror of `complete-task`. A just-tapped chore
 * goes back to `planned`: from `pending_approval` (nothing was credited) or, for an auto-credited chore,
 * from `approved` with the coins clawed back (same household-local day, server-enforced). We
 * optimistically:
 *   1. cancel in-flight day/missions/balance/occurrence queries and snapshot them,
 *   2. flip the occurrence back to `planned` in the day, missions and single-occurrence caches,
 *   3. reverse the coin bump ONLY when it was an auto-credited `approved` chore,
 *   4. carry an `Idempotency-Key` so an offline replay never double-applies,
 *   5. on error surgically restore ONLY this row (never a sibling's in-flight change), and on settle
 *      invalidate so the server reconciles the authoritative state + balance + Missions lane.
 */
export interface UncompleteTaskContext {
  readonly date: string;
  readonly assigneeId: string;
  /** The child whose coin balance to reverse (defaults to the assignee). */
  readonly childId?: string;
}

interface Snapshot {
  day: DayTasksResponse | undefined;
  missions: MissionsResponse | undefined;
  balance: ChildBalanceResponse | undefined;
  occurrence: OccurrenceResponse | undefined;
  dayKey: readonly unknown[];
  missionsKey: readonly unknown[];
  balanceKey: readonly unknown[];
  occurrenceKey: readonly unknown[];
  assigneeId: string;
}

/** Coins are clawed back only for an AUTO-credited chore currently in `approved` (no approval gate). A
 * `pending_approval` chore was never credited, so undoing it moves no coins. */
function coinReversalForFields(
  o: Pick<OccurrenceResponse, 'requiresApproval' | 'status' | 'coinValue'>,
): number {
  if (o.requiresApproval) return 0;
  return o.status === 'approved' ? o.coinValue : 0;
}

function findOcc(
  day: DayTasksResponse | undefined,
  missions: MissionsResponse | undefined,
  occurrence: OccurrenceResponse | undefined,
  occurrenceId: string,
): OccurrenceResponse | undefined {
  if (day) {
    for (const g of day.groups) {
      for (const o of g.occurrences) if (o.id === occurrenceId) return o;
    }
  }
  if (missions) {
    for (const o of missions.missions) if (o.id === occurrenceId) return o;
  }
  if (occurrence && occurrence.id === occurrenceId) return occurrence;
  return undefined;
}

function setStatusInDay(
  day: DayTasksResponse,
  occurrenceId: string,
  status: OccurrenceResponse['status'],
): DayTasksResponse {
  return {
    ...day,
    groups: day.groups.map((g) => ({
      ...g,
      occurrences: g.occurrences.map((o) => (o.id === occurrenceId ? { ...o, status } : o)),
    })),
  };
}

function setStatusInMissions(
  missions: MissionsResponse,
  occurrenceId: string,
  status: OccurrenceResponse['status'],
): MissionsResponse {
  return {
    ...missions,
    missions: missions.missions.map((o) => (o.id === occurrenceId ? { ...o, status } : o)),
  };
}

/** The minimal card identity an undo needs: its optimistic id + the natural key it wires (ADR-0142). */
export interface UncompletableCard {
  readonly id: string;
  readonly templateId: string;
  readonly scheduledLocalDate: string;
  readonly assigneeId: string;
}

export function useUncompleteTaskAction(ctx: UncompleteTaskContext): {
  /** Undo a chore card's completion (deletes its action-log row), addressed by `(templateId, localDate)`. */
  uncomplete: (card: UncompletableCard) => void;
  isPending: boolean;
  isOccurrencePending: (occurrenceId: string) => boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const childId = ctx.childId ?? ctx.assigneeId;
  const dayKey = familyKeys.day(ctx.assigneeId, ctx.date);
  const missionsKey = familyKeys.missions(ctx.assigneeId);
  const balanceKey = familyKeys.balance(childId);

  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set());

  const mutation = useUncompleteTask<Snapshot>({
    onMutate: async ({ optimisticId: occurrenceId }) => {
      setPendingIds((prev) => new Set(prev).add(occurrenceId));
      const occurrenceKey = familyKeys.occurrence(occurrenceId);
      await Promise.all([
        qc.cancelQueries({ queryKey: dayKey }),
        qc.cancelQueries({ queryKey: missionsKey }),
        qc.cancelQueries({ queryKey: balanceKey }),
        qc.cancelQueries({ queryKey: occurrenceKey }),
      ]);
      const day = qc.getQueryData<DayTasksResponse>(dayKey);
      const missions = qc.getQueryData<MissionsResponse>(missionsKey);
      const balance = qc.getQueryData<ChildBalanceResponse>(balanceKey);
      const occurrence = qc.getQueryData<OccurrenceResponse>(occurrenceKey);
      const found = findOcc(day, missions, occurrence, occurrenceId);
      const reversal = found ? coinReversalForFields(found) : 0;

      if (day)
        qc.setQueryData<DayTasksResponse>(dayKey, setStatusInDay(day, occurrenceId, 'planned'));
      if (missions) {
        qc.setQueryData<MissionsResponse>(
          missionsKey,
          setStatusInMissions(missions, occurrenceId, 'planned'),
        );
      }
      if (balance && reversal > 0) {
        qc.setQueryData<ChildBalanceResponse>(balanceKey, {
          ...balance,
          coins: balance.coins - reversal,
        });
      }
      if (occurrence) {
        qc.setQueryData<OccurrenceResponse>(occurrenceKey, { ...occurrence, status: 'planned' });
      }
      return {
        day,
        missions,
        balance,
        occurrence,
        dayKey,
        missionsKey,
        balanceKey,
        occurrenceKey,
        assigneeId: ctx.assigneeId,
      };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      // Restore each captured cache. These reads are keyed to THIS occurrence's assignee/child, and undo
      // is a single-tap action (not a rapid multi-row batch like complete), so restoring the snapshots is
      // safe; `onSettled` reconciles from the server moments later regardless.
      if (context.day) qc.setQueryData(context.dayKey, context.day);
      if (context.missions) qc.setQueryData(context.missionsKey, context.missions);
      if (context.balance) qc.setQueryData(context.balanceKey, context.balance);
      qc.setQueryData(context.occurrenceKey, context.occurrence);
    },
    onSettled: (_data, _err, { optimisticId: occurrenceId }, context) => {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(occurrenceId);
        return next;
      });
      const assigneeIdToInvalidate = context?.assigneeId ?? ctx.assigneeId;
      void qc.invalidateQueries({ queryKey: context?.dayKey ?? dayKey });
      void qc.invalidateQueries({ queryKey: context?.missionsKey ?? missionsKey });
      void qc.invalidateQueries({ queryKey: context?.balanceKey ?? balanceKey });
      void qc.invalidateQueries({ queryKey: familyKeys.weeks(assigneeIdToInvalidate) });
      void qc.invalidateQueries({ queryKey: familyKeys.occurrence(occurrenceId) });
      // Undoing an auto-credited chore retracts its quest progress + hero bond, mirroring complete-task.
      void qc.invalidateQueries({ queryKey: familyKeys.questsActive() });
      invalidateHeroSurfaces(qc);
    },
  });

  const uncomplete = useCallback(
    (card: UncompletableCard) => {
      mutation.reset();
      mutation.mutate({
        optimisticId: card.id,
        templateId: card.templateId,
        localDate: card.scheduledLocalDate,
        assigneeId: card.assigneeId,
        idempotencyKey: newIdempotencyKey(),
      });
    },
    [mutation],
  );

  const isOccurrencePending = useCallback(
    (occurrenceId: string) => pendingIds.has(occurrenceId),
    [pendingIds],
  );

  return {
    uncomplete,
    isPending: pendingIds.size > 0,
    isOccurrencePending,
    error: mutation.error,
  };
}
